import map from 'lodash/map';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import IconLock from '@spothero/icons/lock';
import Form from '@spothero/ui/form';
import FormGroup from '@spothero/ui/form-group';
import Label from '@spothero/ui/label';
import Radio from '@spothero/ui/radio';
import RadioGroup from '@spothero/ui/radio-group';
import Sprite from '@spothero/ui/sprite';
import CheckoutUtils from 'checkout/checkout-utils';
import AddNewCardForm from './AddNewCardForm';

export default class CreditCardSelect extends Component {
    static propTypes = {
        isMonthly: PropTypes.bool,
        user: PropTypes.object,
        selectedCard: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
        ]),
        isPaymentRequestAvailable: PropTypes.bool,
        isApplePayAvailable: PropTypes.bool,
        onCardSelected: PropTypes.func,
    };

    constructor(props) {
        super(props);

        const {
            isMonthly,
            user,
            selectedCard,
            isPaymentRequestAvailable,
            isApplePayAvailable,
        } = props;
        const addApplePay = !isMonthly && isApplePayAvailable;
        const addGooglePay =
            !isMonthly && isPaymentRequestAvailable && !addApplePay;
        const cards = this._getUserCards({
            user,
            selectedCard,
            addApplePay,
            addGooglePay,
        });

        this.state = {
            cards,
            newCreditCard:
                (cards.length === 0 || selectedCard === 'new') &&
                !addApplePay &&
                !addGooglePay,
        };
    }

    _onCardSelected = evt => {
        const {onCardSelected} = this.props;
        const value = evt.target.value;

        this.setState({
            newCreditCard: value === 'new',
        });

        if (onCardSelected) {
            onCardSelected(value);
        }
    };

    _getCardInitials(cardType) {
        switch (cardType) {
            case 'mastercard':
            case 'maestro':
                return 'MC';

            case 'american-express':
                return 'AMEX';

            case 'discover':
                return 'DISC';

            case 'diners-club':
                return 'DC';

            default:
                return cardType.toUpperCase();
        }
    }

    _getUserCards({
        user,
        selectedCard,
        addApplePay = false,
        addGooglePay = false,
    }) {
        const items = [
            addApplePay && {
                label: <Sprite className="Sprite-apple-pay" name="apple-pay" />,
                value: CheckoutUtils.PAYMENT_REQUEST_TYPES.APPLE_PAY,
                checked: selectedCard === 'new',
            },
            addGooglePay && {
                label: (
                    <Sprite className="Sprite-google-pay" name="gpay-mspay" />
                ),
                value: CheckoutUtils.PAYMENT_REQUEST_TYPES.GOOGLE_PAY,
                checked: selectedCard === 'new',
            },
        ].filter(Boolean);

        if (user.creditCards.length) {
            const creditCards = map(user.creditCards, (card, i) => {
                const cardTypeFormatted = card.type
                    .toLowerCase()
                    .replace(/ /g, '-')
                    .replace(/[^\w-]+/g, '');

                return {
                    label: (
                        <>
                            {this._getCardInitials(cardTypeFormatted)}
                            <sup>*</sup>
                            {card.last4}
                        </>
                    ),
                    value: `${card.id}`,
                    checked:
                        (selectedCard !== 'new' && !i) ||
                        selectedCard === card.id,
                    cardTypeFormatted,
                };
            }).concat([
                {
                    label: 'Add new card',
                    value: 'new',
                    checked:
                        addApplePay || addGooglePay
                            ? false
                            : selectedCard === 'new',
                },
            ]);

            items.push(...creditCards);
        } else if (addApplePay || addGooglePay) {
            items.push({
                label: 'Credit or debit card',
                value: 'new',
                checked: false,
            });
        }

        return items;
    }

    _renderCardSprite(cardType) {
        return <Sprite name={`cc-${cardType}`} />;
    }

    submit() {
        if (this.state.newCreditCard) {
            const newCardFormData = this._addNewCardForm.submit();

            if (!newCardFormData) {
                return false;
            }

            return {
                ...newCardFormData,
                selectedCreditCard: 'new',
            };
        } else {
            return this._creditCard.submit();
        }
    }

    render() {
        const {cards, newCreditCard} = this.state;

        return (
            <div className="CreditCardSelect">
                {cards.length > 0 && (
                    <Form
                        ref={node => {
                            this._creditCard = node;
                        }}
                        id="CreditCardSelect-form"
                    >
                        <p>Choose payment method:</p>
                        <FormGroup>
                            <RadioGroup
                                id="CreditCardSelect-card-options"
                                name="selectedCreditCard"
                                className="CreditCardSelect-options"
                                onChange={this._onCardSelected}
                            >
                                {map(cards, (card, i) => {
                                    return (
                                        <Radio
                                            key={i}
                                            label={
                                                <Label>
                                                    {card.cardTypeFormatted &&
                                                        this._renderCardSprite(
                                                            card.cardTypeFormatted
                                                        )}
                                                    {card.label}
                                                </Label>
                                            }
                                            value={card.value}
                                            defaultChecked={card.checked}
                                        />
                                    );
                                })}
                            </RadioGroup>
                        </FormGroup>
                    </Form>
                )}
                {newCreditCard && (
                    <AddNewCardForm
                        ref={node => {
                            this._addNewCardForm = node;
                        }}
                    />
                )}
                <IconLock className="Icon-lock" />
                <small>Secure and encrypted payment</small>
            </div>
        );
    }
}
